import { Button, Popper } from '@material-ui/core'
import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { fromEvent, Subscription } from 'rxjs'
import { UsersByExtension } from '../../../../i18n'
import { EventNames } from '../../../NonReact/constants/EventNames'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import Icon, { IconName } from '../../Common/Components/Icon'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ReactState } from '../../Common/Types/Types'
import { deleteUser } from '../../../NonReact/common/API/EntityAPIClient'

type Props = {
    updateExtensionErrors: (index: number, error?: string) => void
    currentIndex: number
    isConfirmDelete: boolean
    setConfirmDelete: (isConfirmDelete: boolean) => void
    updateDeviceUsers: (index: number, users: string[]) => void
    user: string
    originalDevice: DevicesResponse
    onRemove?: () => void
    isLoading?: boolean
}

export const EntryRemove: FC<Props> = ({
    updateExtensionErrors,
    currentIndex,
    isConfirmDelete,
    setConfirmDelete,
    updateDeviceUsers,
    user,
    originalDevice,
    onRemove,
    isLoading
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [eventSubscription, setEventSubscription]: ReactState<Definable<Subscription>> =
        useState<Definable<Subscription>>(undefined)
    const [crossPopperOpen, setCrossPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    const buttonRef: MutableRefObject<null> = useRef<null>(null)

    const removeUserClickHandler = (): void => {
        if (isConfirmDelete) {
            updateExtensionErrors(currentIndex, undefined)
            setConfirmDelete(false)

            deleteUser(user)
                .then((): void => {
                    updateDeviceUsers(
                        currentIndex,
                        originalDevice?.Users?.filter((u: string): boolean => u !== user) ?? []
                    )

                    if (onRemove) {
                        onRemove()
                    }
                })
                .catch(displayError)
        } else {
            setConfirmDelete(true)
        }
    }

    const handleClickAway = (event: MouseEvent): void => {
        if (event) {
            if (buttonRef.current && !(buttonRef.current as unknown as Node).contains(event.target as Node)) {
                setConfirmDelete(false)
            } else {
                removeUserClickHandler()
            }
        }
    }

    useEffect((): void => {
        if (isConfirmDelete) {
            setEventSubscription(
                fromEvent(document, EventNames.MOUSEDOWN).subscribe((event: Event): void =>
                    handleClickAway(event as MouseEvent)
                )
            )
        } else if (eventSubscription) {
            eventSubscription.unsubscribe()
            setEventSubscription(undefined)
        }
    }, [isConfirmDelete])

    return (
        <>
            <Button
                onMouseOver={(): void => {
                    if (!isConfirmDelete) {
                        setCrossPopperOpen(true)
                    }
                }}
                onMouseOut={(): void => setCrossPopperOpen(false)}
                className='ubeu-remove ube-entry-action'
                ref={buttonRef}
                onClick={removeUserClickHandler}
            >
                {isConfirmDelete ? <Icon iconName={IconName.DELETE} /> : <Icon iconName={IconName.X} />}
            </Button>
            {!isLoading ? (
                <Popper
                    anchorEl={buttonRef.current}
                    open={isConfirmDelete || crossPopperOpen}
                    className='ubeu-remove-confirm-popper'
                    placement='top'
                >
                    <div className='ubeu-remove-confirm-message'>
                        {crossPopperOpen && !isConfirmDelete
                            ? language.get(UsersByExtension.UsersByExtensions_UserFields_Cross_Tooltip)
                            : language.get(UsersByExtension.UsersByExtensions_ConfirmUserDelete)}
                    </div>
                </Popper>
            ) : (
                <></>
            )}
        </>
    )
}
